import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import TimelineIcon from '@mui/icons-material/Timeline';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { assignSgrDefaultConcentration } from 'lib/api/creditLine';
import { SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import InfoCard from 'lib/designSystem/InfoCard';
import PageHeader from 'lib/designSystem/PageHeader';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

export const SGR_SUB_PRODUCT_CONCENTRATIONS = {
  'offer-seriousness': {
    fixed: 0.5,
    variable: 5,
    amount: 800000,
    maxDocumentAmount: 800000,
  },
  'faithful-compliance': {
    fixed: 0.5,
    variable: 4.5,
    amount: 15000000,
    maxDocumentAmount: 15000000,
  },
  advance: {
    fixed: 0.5,
    variable: 5,
    amount: 800000,
    maxDocumentAmount: 800000,
  },
  'proper-performance': {
    fixed: 0.5,
    variable: 5,
    amount: 800000,
    maxDocumentAmount: 800000,
  },
  'faithful-compliance-performance': {
    fixed: 0.5,
    variable: 5,
    amount: 800000,
    maxDocumentAmount: 800000,
  },
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  id?: number;
  subProductId: string;
}
const AutocompleteConcentrationDialog: React.FC<Props> = ({
  open,
  onClose,
  id,
  subProductId,
}) => {
  const dispatch = useDispatch();
  const { isPending, mutate } = assignSgrDefaultConcentration(dispatch);

  const concentrationConfig =
    subProductId &&
    SGR_SUB_PRODUCT_CONCENTRATIONS[
      subProductId as keyof typeof SGR_SUB_PRODUCT_CONCENTRATIONS
    ];

  if (!concentrationConfig) return null;

  const { fixed, variable, amount, maxDocumentAmount } = concentrationConfig;
  const productLabel = SGR_TYPES.find(
    (type) => type.value === subProductId,
  )?.label;

  const handleAssign = () => {
    mutate(
      { id, subProductId },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <PageHeader.TitleOnly
          title="Asignar Concentración"
          iconType="documents"
          subtitle={`Concentración base para subproducto ${productLabel}`}
        />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InfoCard
              icon={<PercentIcon />}
              label="Tasa Fija"
              value={fixed.toString()}
              color="blue"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<TimelineIcon />}
              label="Tasa Variable"
              value={variable.toString()}
              color="green"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<AttachMoneyIcon />}
              label="Monto"
              value={formatMoney(amount)}
              color="orange"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<DescriptionIcon />}
              label="Monto Máximo Documento"
              value={formatMoney(maxDocumentAmount)}
              color="red"
            />
          </Grid>
        </Grid>
        <Alert
          severity="error"
          icon={false}
          sx={{
            mt: 3,
            borderRadius: 0.5,
            backgroundColor: 'rgba(255, 160, 0, 0.08)',
            border: '1px solid rgba(255, 160, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <WarningIcon
              sx={{ fontSize: '1.2rem', color: 'warning.main', mr: 1 }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', display: 'inline-block' }}
            >
              Se creara una concentración base para el subproducto{' '}
              {productLabel}
            </Typography>
          </Box>
        </Alert>
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={handleAssign}
          color="primary"
          loading={isPending}
          disabled={isPending}
        >
          Asignar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AutocompleteConcentrationDialog;
